import type { Job, JobsTab } from '../types'
import { isJobLate, isJobOnDate, isJobUnassigned, type JobDisplayStatus } from './job-status'

/**
 * Jobs per tab. Unassigned: nobody assigned, overdue bookings first. Today: the selected day.
 * Late: running late. The selected job is pinned into every tab so it never disappears from
 * under the admin while selected (e.g. it gets its first worker while on "Unassigned"); a job
 * removing its last worker lands in "Unassigned" on its own.
 */
export function buildJobLists(
  jobs: Job[],
  {
    now,
    dateKey,
    timeZone,
    displayById,
    pinnedJob = null,
  }: {
    now: number
    dateKey: string
    timeZone: string
    displayById: Map<string, JobDisplayStatus>
    pinnedJob?: Job | null
  },
): Record<JobsTab, Job[]> {
  const byStart = (a: Job, b: Job) => Date.parse(a.scheduledStart) - Date.parse(b.scheduledStart)
  const overdueFirst = (a: Job, b: Job) => {
    const aOverdue = displayById.get(a.id)?.kind === 'overdue' ? 0 : 1
    const bOverdue = displayById.get(b.id)?.kind === 'overdue' ? 0 : 1
    return aOverdue - bOverdue || byStart(a, b)
  }
  const lists: Record<JobsTab, Job[]> = {
    unassigned: jobs.filter((job) => isJobUnassigned(job, now)).sort(overdueFirst),
    today: jobs.filter((job) => isJobOnDate(job, dateKey, timeZone)),
    late: jobs.filter((job) => isJobLate(job, now, timeZone)),
  }
  if (pinnedJob) {
    ;(Object.keys(lists) as JobsTab[]).forEach((key) => {
      if (lists[key].some((job) => job.id === pinnedJob.id)) return
      lists[key] = [...lists[key], pinnedJob].sort(key === 'unassigned' ? overdueFirst : byStart)
    })
  }
  return lists
}
